import React from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Download, Trash2, Globe, Save, Copy, Check, Users } from "lucide-react";
import { api_endpoints } from "@/lib/api";
import { PageHeader, Btn, Card, Eyebrow, Field } from "@/components/app/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import i18n from "@/lib/i18n";

const LOCALES = [
    { value: "en", label: "English" },
    { value: "cs", label: "Čeština" },
    { value: "sk", label: "Slovenčina" },
    { value: "de", label: "Deutsch" },
    { value: "pl", label: "Polski" },
    { value: "fr", label: "Français" },
    { value: "it", label: "Italiano" },
    { value: "es", label: "Español" },
    { value: "uk", label: "Українська" },
    { value: "ru", label: "Русский" },
];

const COUNTRIES = [
    { value: "CZ", label: "Czech Republic" },
    { value: "SK", label: "Slovakia" },
    { value: "DE", label: "Germany" },
    { value: "AT", label: "Austria" },
    { value: "PL", label: "Poland" },
    { value: "FR", label: "France" },
    { value: "IT", label: "Italy" },
    { value: "ES", label: "Spain" },
    { value: "NL", label: "Netherlands" },
    { value: "BE", label: "Belgium" },
    { value: "UK", label: "United Kingdom" },
    { value: "IE", label: "Ireland" },
    { value: "DK", label: "Denmark" },
    { value: "SE", label: "Sweden" },
    { value: "NO", label: "Norway" },
    { value: "FI", label: "Finland" },
    { value: "PT", label: "Portugal" },
    { value: "GR", label: "Greece" },
    { value: "RO", label: "Romania" },
    { value: "HU", label: "Hungary" },
];

export default function Settings() {
    const { t } = useTranslation();
    const [profile, setProfile] = React.useState({ country: "", locale: "en", unitSystem: "metric" });
    const [draft, setDraft] = React.useState({ country: "", locale: "en", unitSystem: "metric" });
    const [loading, setLoading] = React.useState(true);
    const [exporting, setExporting] = React.useState(false);
    const [referral, setReferral] = React.useState(null);
    const [referralLoading, setReferralLoading] = React.useState(true);
    const [referralError, setReferralError] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    function loadReferral() {
        setReferralLoading(true);
        setReferralError(false);
        api_endpoints
            .referral()
            .then((r) => setReferral(r))
            .catch(() => setReferralError(true))
            .finally(() => setReferralLoading(false));
    }

    React.useEffect(() => {
        api_endpoints
            .profile()
            .then((p) => {
                setProfile(p);
                setDraft(p);
                if (p.unitSystem) localStorage.setItem("spotcar-units", p.unitSystem);
                if (p.locale) {
                    i18n.changeLanguage(p.locale);
                    localStorage.setItem("spotcar-locale", p.locale);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        loadReferral();
    }, []);

    function retryReferral() {
        loadReferral();
    }

    function copyReferralLink() {
        if (!referral?.referralLink) return;
        navigator.clipboard.writeText(referral.referralLink)
            .then(() => {
                setCopied(true);
                toast.success(t("settings.linkCopied"));
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => toast.error(t("settings.copyFailed")));
    }

    async function save() {
        try {
            const r = await api_endpoints.updateProfile(draft);
            setProfile(r);
            setDraft(r);
            i18n.changeLanguage(r.locale);
            localStorage.setItem("spotcar-locale", r.locale);
            localStorage.setItem("spotcar-units", r.unitSystem);
            toast.success(i18n.t("settings.saved"));
        } catch (e) {
            toast.error(e.message);
        }
    }

    async function exportData() {
        setExporting(true);
        try {
            const r = await api_endpoints.exportProfile();
            const blob = new Blob([JSON.stringify(r, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `spotcar-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(t("settings.exportSuccess"));
        } catch (e) {
            toast.error(e.message);
        } finally {
            setExporting(false);
        }
    }

    async function deleteAccount() {
        const phrase = window.prompt(t("settings.deleteConfirm"));
        if (phrase !== "DELETE") return;
        try {
            await api_endpoints.deleteProfile();
            toast.success(t("settings.accountDeleted"));
            setTimeout(() => (window.location.href = "/"), 800);
        } catch (e) {
            toast.error(e.message);
        }
    }

    const dirty = draft.country !== profile.country || draft.locale !== profile.locale || draft.unitSystem !== profile.unitSystem;

    return (
        <div>
            <PageHeader
                eyebrow={t("settings.eyebrow")}
                title={t("settings.title")}
            />

            <div className="p-6 md:p-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                {/* Preferences */}
                <Card className="p-6" testid="prefs-card">
                    <Eyebrow>── {t("settings.prefsTitle")}</Eyebrow>
                    <h3 className="mt-2 font-display text-xl uppercase tracking-tight">
                        {t("settings.prefsHeading")}
                    </h3>
                    {loading ? (
                        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mt-4">
                            {t("common.loading")}
                        </div>
                    ) : (
                        <>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <Field label={t("settings.language")} testid="locale">
                                    <Select value={draft.locale} onValueChange={(v) => setDraft((d) => ({ ...d, locale: v }))}>
                                        <SelectTrigger data-testid="locale-select" className="w-full border-2 border-black bg-white dark:bg-zinc-900 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em]">
                                            <SelectValue className="ph-mask" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {LOCALES.map((l) => (
                                                <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="mt-1 font-mono text-[10px] text-zinc-500">{t("settings.languageHint")}</p>
                                </Field>
                                <Field label={t("settings.country")} testid="country">
                                    <Select value={draft.country || "__none__"} onValueChange={(v) => setDraft((d) => ({ ...d, country: v === "__none__" ? "" : v }))}>
                                        <SelectTrigger data-testid="country-select" className="w-full border-2 border-black bg-white dark:bg-zinc-900 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em]">
                                            <SelectValue className="ph-mask" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">{t("settings.countryNone")}</SelectItem>
                                            {COUNTRIES.map((c) => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="mt-1 font-mono text-[10px] text-zinc-500">{t("settings.countryHint")}</p>
                                </Field>
                                <Field label={t("settings.unitSystem")} testid="unit-system">
                                    <Select value={draft.unitSystem || "metric"} onValueChange={(v) => setDraft((d) => ({ ...d, unitSystem: v }))}>
                                        <SelectTrigger data-testid="unit-system-select" className="w-full border-2 border-black bg-white dark:bg-zinc-900 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em]">
                                            <SelectValue className="ph-mask" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="metric">{t("settings.metric")}</SelectItem>
                                            <SelectItem value="imperial">{t("settings.imperial")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="mt-1 font-mono text-[10px] text-zinc-500">{t("settings.unitSystemHint")}</p>
                                </Field>
                            </div>
                            <div className="mt-5">
                                <Btn
                                    variant="primary"
                                    icon={Save}
                                    onClick={save}
                                    disabled={!dirty}
                                    testid="save-prefs"
                                >
                                    {t("settings.savePrefs")}
                                </Btn>
                            </div>
                        </>
                    )}
                </Card>

                {/* Referral Program */}
                <Card className="p-6" testid="referral-card">
                    <Eyebrow>── {t("settings.referralTitle")}</Eyebrow>
                    <h3 className="mt-2 font-display text-xl uppercase tracking-tight">
                        {t("settings.referralHeading")}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-400 max-w-md">
                        {t("settings.referralDescription")}
                    </p>
                    {referralLoading ? (
                        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mt-4">
                            {t("common.loading")}
                        </div>
                    ) : referralError ? (
                        <div className="mt-4 flex items-center gap-3">
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-red-500">
                                {t("settings.referralError")}
                            </p>
                            <Btn variant="outline" onClick={retryReferral} testid="retry-referral">
                                {t("settings.retry")}
                            </Btn>
                        </div>
                    ) : (
                        <>
                            <div className="mt-4">
                                <div
                                    role="group"
                                    aria-labelledby="referral-link-label"
                                    data-testid="field-referral-link"
                                >
                                    <div
                                        id="referral-link-label"
                                        className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-1.5"
                                    >
                                        {t("settings.referralLink")}
                                    </div>
                                    <div className="flex w-full min-w-0 flex-col items-stretch gap-2 sm:flex-row">
                                        <code
                                            className="min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap border-2 border-black dark:border-zinc-600 bg-zinc-100 dark:bg-zinc-800 px-3 py-2 font-mono text-sm tracking-tight text-zinc-500 dark:text-zinc-400 select-all cursor-default sm:flex-1"
                                            data-testid="referral-link-value"
                                        >
                                            {referral?.referralLink ?? ""}
                                        </code>
                                        <Btn
                                            variant="outline"
                                            icon={copied ? Check : Copy}
                                            onClick={copyReferralLink}
                                            disabled={!referral?.referralLink}
                                            testid="copy-referral-link"
                                            className="flex-shrink-0 whitespace-nowrap"
                                        >
                                            {copied ? t("settings.linkCopied") : t("settings.copyLink")}
                                        </Btn>
                                    </div>
                                </div>
                            </div>
                            {referral && (
                                <p className="mt-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.1em] text-zinc-500">
                                    <Users className="w-3 h-3" />
                                    <span>{t("settings.successfulReferrals", { count: referral.successfulReferrals })}</span>
                                </p>
                            )}
                        </>
                    )}
                </Card>
                </div>
                <div className="space-y-6">

                {/* GDPR Export */}
                <Card className="p-6" testid="export-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <Eyebrow>── {t("settings.gdprTitle")}</Eyebrow>
                            <h3 className="mt-2 font-display text-xl uppercase tracking-tight">
                                {t("settings.gdprHeading")}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-700 max-w-md">
                                {t("settings.gdprDescription")}
                            </p>
                        </div>
                        <Btn
                            variant="outline"
                            icon={Download}
                            onClick={exportData}
                            disabled={exporting}
                            testid="export-data"
                        >
                            {exporting ? t("settings.exporting") : t("settings.exportJson")}
                        </Btn>
                    </div>
                </Card>

                {/* Danger zone */}
                <Card className="p-6 border-[#FF3B30]" testid="danger-card">
                    <Eyebrow>── {t("settings.dangerTitle")}</Eyebrow>
                    <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <h3 className="font-display text-xl uppercase tracking-tight">
                                {t("settings.dangerHeading")}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-700 max-w-md">
                                {t("settings.dangerDescription")}
                            </p>
                        </div>
                        <Btn
                            variant="danger"
                            icon={Trash2}
                            onClick={deleteAccount}
                            testid="delete-account"
                        >
                            {t("settings.deleteAccount")}
                        </Btn>
                    </div>
                </Card>
                </div>
                </div>
            </div>
        </div>
    );
}
